/**
 * Reporting aggregation — pure functions.
 *
 * Reference implementation of every figure the dashboard shows. Kept framework-
 * and React-free so it can be (a) unit-tested against the source workbook's
 * published numbers (aggregate.test.ts), (b) used by the dev/mock data path, and
 * (c) ported verbatim to the WFP API for the server-side, de-identified
 * /reports/aggregates endpoint.
 *
 * Rules (match docs/Sample analysis.xlsx):
 *   • Distributions exclude not-assessed from the denominator (proportions are
 *     over assessed instances only).
 *   • Pou / domain roll-ups sum the (whānau × subdomain) instances in the group.
 *   • Transition matrices and "analysis of change" count only instances assessed
 *     (risk / change / protective) at BOTH entry and exit — you cannot classify a
 *     movement without both endpoints. (This is why Te Pou Aotūroa resolves to 312
 *     assessed-at-both, not the workbook's 313 exit-assessed annotation: one
 *     child-education instance is assessed at exit but not entry.)
 */
import { SUBDOMAINS, POUS, DOMAINS } from "../framework/data";
import type { FactorStateKind, SubdomainId } from "../framework/types";
import type {
  ChangeAnalysis,
  ChangeCategory,
  CohortDimension,
  CohortFilters,
  DomainAggregate,
  EntryExit,
  Facet,
  Phase,
  PouAggregate,
  ReportAggregates,
  ReportProfile,
  StateCounts,
  StateProportion,
  SubdomainAggregate,
  TransitionMatrix,
  WhanauStateEntryExit,
} from "./reportTypes";
import {
  classifyWhanauState,
  emptyWhanauStateDistribution,
  emptyWhanauStateTransition,
  tallyStates,
} from "./whanauState";

const STATE_INDEX: Record<"risk" | "change" | "protective", number> = {
  risk: 0,
  change: 1,
  protective: 2,
};

function stateOf(
  profile: ReportProfile,
  phase: Phase,
  subId: SubdomainId,
): FactorStateKind {
  return profile[phase][subId] ?? "not-assessed";
}

function isAssessed(
  kind: FactorStateKind,
): kind is "risk" | "change" | "protective" {
  return kind !== "not-assessed";
}

// -------- filtering --------

export function profileMatchesFilters(
  p: ReportProfile,
  f: CohortFilters,
): boolean {
  if (f.location.length && !(p.location && f.location.includes(p.location)))
    return false;
  if (f.iwi.length && !(p.iwi && f.iwi.includes(p.iwi))) return false;
  if (f.ethnicity.length && !(p.ethnicity && f.ethnicity.includes(p.ethnicity)))
    return false;
  if (f.service.length && !(p.service && f.service.includes(p.service)))
    return false;
  if (
    f.durationBand.length &&
    !(p.durationBand && f.durationBand.includes(p.durationBand))
  )
    return false;
  return true;
}

export function anyFilterActive(f: CohortFilters): boolean {
  return (
    f.location.length > 0 ||
    f.iwi.length > 0 ||
    f.ethnicity.length > 0 ||
    f.service.length > 0 ||
    f.durationBand.length > 0
  );
}

// -------- distributions --------

function countStates(
  profiles: readonly ReportProfile[],
  subIds: readonly SubdomainId[],
  phase: Phase,
): StateCounts {
  const c: StateCounts = { risk: 0, change: 0, protective: 0, notAssessed: 0 };
  for (const p of profiles) {
    for (const subId of subIds) {
      const kind = stateOf(p, phase, subId);
      if (kind === "not-assessed") c.notAssessed += 1;
      else c[kind] += 1;
    }
  }
  return c;
}

function proportion(c: StateCounts): StateProportion {
  const assessed = c.risk + c.change + c.protective;
  if (assessed === 0) return { risk: 0, change: 0, protective: 0 };
  return {
    risk: c.risk / assessed,
    change: c.change / assessed,
    protective: c.protective / assessed,
  };
}

function entryExit(
  profiles: readonly ReportProfile[],
  subIds: readonly SubdomainId[],
): EntryExit {
  const entry = countStates(profiles, subIds, "entry");
  const exit = countStates(profiles, subIds, "exit");
  return {
    entry,
    exit,
    entryProportion: proportion(entry),
    exitProportion: proportion(exit),
    assessedEntry: entry.risk + entry.change + entry.protective,
    assessedExit: exit.risk + exit.change + exit.protective,
  };
}

// -------- transition matrix --------

function transitionFor(
  profiles: readonly ReportProfile[],
  subId: SubdomainId,
): TransitionMatrix {
  const matrix = [
    [0, 0, 0],
    [0, 0, 0],
    [0, 0, 0],
  ];
  let total = 0;
  for (const p of profiles) {
    const a = stateOf(p, "entry", subId);
    const b = stateOf(p, "exit", subId);
    if (isAssessed(a) && isAssessed(b)) {
      matrix[STATE_INDEX[a]][STATE_INDEX[b]] += 1;
      total += 1;
    }
  }
  return { matrix, total };
}

// -------- analysis of change --------

function classify(
  a: "risk" | "change" | "protective",
  b: "risk" | "change" | "protective",
): ChangeCategory {
  if (a === b) {
    if (a === "risk") return "remainedRisk";
    if (a === "change") return "remainedChange";
    return "remainedProtective";
  }
  if (a === "risk" && b === "change") return "riskToChange";
  if (a === "risk" && b === "protective") return "riskToProtective";
  if (a === "change" && b === "protective") return "changeToProtective";
  return "regressed";
}

function changeAnalysis(
  profiles: readonly ReportProfile[],
  subIds: readonly SubdomainId[],
): ChangeAnalysis {
  const counts: Record<ChangeCategory, number> = {
    remainedRisk: 0,
    remainedChange: 0,
    remainedProtective: 0,
    riskToChange: 0,
    riskToProtective: 0,
    changeToProtective: 0,
    regressed: 0,
  };
  let total = 0;
  for (const p of profiles) {
    for (const subId of subIds) {
      const a = stateOf(p, "entry", subId);
      const b = stateOf(p, "exit", subId);
      if (isAssessed(a) && isAssessed(b)) {
        counts[classify(a, b)] += 1;
        total += 1;
      }
    }
  }
  const proportionOf: Record<ChangeCategory, number> = {
    remainedRisk: 0,
    remainedChange: 0,
    remainedProtective: 0,
    riskToChange: 0,
    riskToProtective: 0,
    changeToProtective: 0,
    regressed: 0,
  };
  if (total > 0) {
    for (const key of Object.keys(counts) as ChangeCategory[]) {
      proportionOf[key] = counts[key] / total;
    }
  }
  const improved =
    counts.riskToChange + counts.riskToProtective + counts.changeToProtective;
  const remained =
    counts.remainedRisk + counts.remainedChange + counts.remainedProtective;
  return {
    counts,
    proportion: proportionOf,
    total,
    improved,
    remained,
    regressed: counts.regressed,
  };
}

// -------- facets --------

function facetFor(
  profiles: readonly ReportProfile[],
  pick: (p: ReportProfile) => string | null,
): Facet[] {
  const tally = new Map<string, number>();
  for (const p of profiles) {
    const v = pick(p);
    if (v == null || v === "") continue;
    tally.set(v, (tally.get(v) ?? 0) + 1);
  }
  return [...tally.entries()]
    .map(([value, count]) => ({ value, count }))
    .sort((x, y) => y.count - x.count || x.value.localeCompare(y.value));
}

function buildFacets(
  profiles: readonly ReportProfile[],
): Record<CohortDimension, Facet[]> {
  return {
    location: facetFor(profiles, (p) => p.location),
    iwi: facetFor(profiles, (p) => p.iwi),
    ethnicity: facetFor(profiles, (p) => p.ethnicity),
    service: facetFor(profiles, (p) => p.service),
    durationBand: facetFor(profiles, (p) => p.durationBand),
  };
}

// -------- top-level --------

const SUB_IDS: SubdomainId[] = SUBDOMAINS.map((s) => s.id);

/**
 * Whānau-level dominant state at entry vs exit + the transition (ADR-010). Each
 * whānau collapses to one state per phase; every cohort whānau counts (incl.
 * 'unassessed'), so entry.total = exit.total = cohort size.
 */
function whanauStateEntryExit(
  profiles: readonly ReportProfile[],
): WhanauStateEntryExit {
  const entry = emptyWhanauStateDistribution();
  const exit = emptyWhanauStateDistribution();
  const transition = emptyWhanauStateTransition();
  for (const p of profiles) {
    const e = classifyWhanauState(tallyStates(Object.values(p.entry)));
    const x = classifyWhanauState(tallyStates(Object.values(p.exit)));
    entry[e] += 1;
    entry.total += 1;
    exit[x] += 1;
    exit.total += 1;
    transition[e][x] += 1;
  }
  return { entry, exit, transition, total: profiles.length };
}

/**
 * Compute the full de-identified aggregate payload from a set of profiles and an
 * active cohort filter. Facets are computed over the UNFILTERED set so every
 * filter option stays visible.
 */
export function aggregate(
  profiles: readonly ReportProfile[],
  filters: CohortFilters,
): ReportAggregates {
  const cohort = profiles.filter((p) => profileMatchesFilters(p, filters));

  const bySubdomain: SubdomainAggregate[] = SUBDOMAINS.map((s) => ({
    subdomainId: s.id,
    pouId: s.pouId,
    domainId: s.domainId,
    ...entryExit(cohort, [s.id]),
    transition: transitionFor(cohort, s.id),
    analysis: changeAnalysis(cohort, [s.id]),
  }));

  const byPou: PouAggregate[] = POUS.map((pou) => {
    const ids = SUBDOMAINS.filter((s) => s.pouId === pou.id).map((s) => s.id);
    return {
      pouId: pou.id,
      ...entryExit(cohort, ids),
      analysis: changeAnalysis(cohort, ids),
    };
  });

  const byDomain: DomainAggregate[] = DOMAINS.map((domain) => {
    const ids = SUBDOMAINS.filter((s) => s.domainId === domain.id).map(
      (s) => s.id,
    );
    return {
      domainId: domain.id,
      ...entryExit(cohort, ids),
      analysis: changeAnalysis(cohort, ids),
    };
  });

  return {
    totalWhanau: cohort.length,
    filtered: anyFilterActive(filters),
    population: {
      ...entryExit(cohort, SUB_IDS),
      analysis: changeAnalysis(cohort, SUB_IDS),
    },
    bySubdomain,
    byPou,
    byDomain,
    whanauState: whanauStateEntryExit(cohort),
    facets: buildFacets(profiles),
    generatedAt: new Date().toISOString(),
  };
}
